import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import { CircularProgress } from './CircularProgress';
import { Confetti } from './Confetti';
import { RainCloud } from './RainCloud';
import { QuestionRenderer } from './question-types/QuestionRenderer';
import type { Question } from '@/types';
import { useCreateSession, useRecordResponse, useCompleteSession } from '@/queries/quiz-sessions';
import { useMathLivePrefetch } from '@/hooks/useMathLivePrefetch';
import { shuffleArray } from '@/lib/utils';
import styles from './QuizSession.module.scss';

interface QuizSessionProps {
  questions: Question[];
  title?: string;
  quizletId?: string;
  isDailyKnowledge?: boolean;
  skipAnalytics?: boolean;
  onComplete?: (score: number, correct: number, total: number) => void;
  onExit?: () => void;
}

export function QuizSession({
  questions,
  title = 'Quiz',
  quizletId,
  isDailyKnowledge = false,
  skipAnalytics = false,
  onComplete,
  onExit,
}: QuizSessionProps) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState<unknown>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState<{ correct: number; total: number }>({ correct: 0, total: 0 });
  const [quizComplete, setQuizComplete] = useState(false);
  const [isAnswered, setIsAnswered] = useState(false);

  // Shuffle questions once on mount for randomized order
  const [shuffledQuestions, setShuffledQuestions] = useState<Question[]>(() =>
    shuffleArray(questions)
  );

  // Session tracking state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Session tracking hooks
  const createSessionMutation = useCreateSession();
  const recordResponseMutation = useRecordResponse();
  const completeSessionMutation = useCompleteSession();

  // Prefetch MathLive if quiz contains math questions
  const mathLiveReady = useMathLivePrefetch(shuffledQuestions);

  const currentQuestion = shuffledQuestions[currentIndex];
  const progress = ((currentIndex + 1) / shuffledQuestions.length) * 100;
  const submitQuestionTypes = ['fill_blank', 'mix_match', 'math', 'code'];

  // Create session on mount (skip if analytics are disabled)
  useEffect(() => {
    if (skipAnalytics) return;

    createSessionMutation.mutate(
      {
        quizlet_id: quizletId,
        is_daily_knowledge: isDailyKnowledge,
      },
      {
        onSuccess: (response) => {
          setSessionId(response.sessionId);
        },
      }
    );
  }, [skipAnalytics]);

  const handleAnswer = (answer: unknown) => {
    setUserAnswer(answer);

    // Auto-submit for multiple choice questions only
    if (currentQuestion.question_type === 'multiple_choice' && !isAnswered) {
      handleSubmit(answer);
    }
  };

  const handleSubmit = (answer?: unknown) => {
    const answerToCheck = answer ?? userAnswer;

    if (!isAnswered && answerToCheck !== null) {
      setIsAnswered(true);
      setShowExplanation(true);

      // Calculate time taken for this question
      const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);

      // Record response to backend (don't block on failure, skip if analytics disabled)
      if (sessionId && !skipAnalytics) {
        recordResponseMutation.mutate({
          sessionId,
          data: {
            question_id: currentQuestion.id,
            user_answer: answerToCheck,
            time_taken_seconds: timeTaken,
          },
        });
      }

      // Check if answer is correct based on question type (for immediate UI feedback)
      const isCorrect = checkAnswer(currentQuestion, answerToCheck);
      setScore(prev => ({
        correct: prev.correct + (isCorrect ? 1 : 0),
        total: prev.total + 1,
      }));
    }
  };

  const checkAnswer = (question: Question, answer: unknown): boolean => {
    switch (question.question_type) {
      case 'multiple_choice': {
        const selectedIds = answer as string[];
        const correctOptions = question.content.options.filter(opt => opt.isCorrect);
        const correctIds = correctOptions.map(opt => opt.id);

        // Check if all correct options are selected and no incorrect ones
        return (
          selectedIds.length === correctIds.length &&
          selectedIds.every(id => correctIds.includes(id))
        );
      }

      case 'fill_blank': {
        const answers = answer as Record<number, string>;
        return question.content.blanks.every(blank => {
          const userValue = answers[blank.position]?.trim() || '';
          if (!userValue) return false;

          const caseSensitive = question.content.caseSensitive || false;
          return blank.acceptedAnswers.some(accepted =>
            caseSensitive
              ? accepted === userValue
              : accepted.toLowerCase() === userValue.toLowerCase()
          );
        });
      }

      case 'mix_match': {
        const pairs = answer as Array<{ leftId: string; rightId: string }>;
        // All pairs must be correct
        return (
          pairs.length === question.content.correctPairs.length &&
          pairs.every(pair =>
            question.content.correctPairs.some(
              cp => cp.leftId === pair.leftId && cp.rightId === pair.rightId
            )
          )
        );
      }

      case 'math': {
        const userValue = (answer as string)?.trim() || '';
        if (!userValue) return false;

        // Basic normalization: remove all whitespace for comparison
        // This handles simple differences like "x + y" vs "x+y"
        const normalizedUser = userValue.replace(/\s/g, '');

        return question.content.acceptedAnswers.some(accepted => {
          const normalizedAccepted = accepted.replace(/\s/g, '');
          return normalizedUser === normalizedAccepted;
        });
      }

      default:
        return false;
    }
  };

  const handleNextQuestion = () => {
    if (currentIndex < shuffledQuestions.length - 1) {
      // Move to next question
      setCurrentIndex(prev => prev + 1);
      setUserAnswer(null);
      setShowExplanation(false);
      setIsAnswered(false);

      // Reset timer for next question
      setQuestionStartTime(Date.now());
    } else {
      // Quiz complete
      setQuizComplete(true);

      // Complete session in backend (skip if analytics disabled)
      if (sessionId && !skipAnalytics) {
        completeSessionMutation.mutate(sessionId);
      }

      // Call onComplete callback
      onComplete?.(
        (score.correct / score.total) * 100,
        score.correct,
        score.total
      );
    }
  };

  if (quizComplete) {
    const percentCorrect = (score.correct / score.total) * 100;
    const showConfetti = percentCorrect >= 80;

    const handleRetry = () => {
      setCurrentIndex(0);
      setUserAnswer(null);
      setShowExplanation(false);
      setScore({ correct: 0, total: 0 });
      setQuizComplete(false);
      setIsAnswered(false);
      setSessionId(null);
      setQuestionStartTime(Date.now());

      // Re-shuffle questions for a fresh quiz experience
      setShuffledQuestions(shuffleArray(questions));

      // Only create new session if analytics are enabled
      if (!skipAnalytics) {
        createSessionMutation.mutate(
          {
            quizlet_id: quizletId,
            is_daily_knowledge: isDailyKnowledge,
          },
          {
            onSuccess: (response) => {
              setSessionId(response.sessionId);
            },
          }
        );
      }
    };

    const handleBack = () => {
      if (onExit) {
        onExit();
      } else {
        navigate({ to: '/quizlets' });
      }
    };

    return (
      <div className={styles.quizSession}>
        {showConfetti && <Confetti />}
        {!showConfetti && <RainCloud />}
        <motion.div
          className={`${styles.quizSession__card} ${styles.quizSession__results}`}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className={styles.quizSession__resultsTitle}>
            {showConfetti ? 'Amazing Job!' : 'Keep Practicing!'}
          </h2>
          <p className={styles.quizSession__resultsMessage}>
            {showConfetti
              ? "You've mastered this topic! Great work!"
              : "You're making progress! Review and try again."}
          </p>

          <div className={styles.quizSession__circularProgress}>
            <CircularProgress percent={percentCorrect} />
          </div>

          <div className={styles.quizSession__stats}>
            <div className={styles.quizSession__stat}>
              <div className={styles.quizSession__statValue}>{score.correct}</div>
              <div className={styles.quizSession__statLabel}>Correct</div>
            </div>
            <div className={styles.quizSession__stat}>
              <div className={styles.quizSession__statValue}>{score.total - score.correct}</div>
              <div className={styles.quizSession__statLabel}>Incorrect</div>
            </div>
            <div className={styles.quizSession__stat}>
              <div className={styles.quizSession__statValue}>{score.total}</div>
              <div className={styles.quizSession__statLabel}>Total</div>
            </div>
          </div>

          <div className={styles.quizSession__resultsActions}>
            <button
              className={`${styles.quizSession__button} ${styles['quizSession__button--secondary']}`}
              onClick={handleBack}
            >
              {onExit ? 'Back to Review Center' : 'Back to Quizzes'}
            </button>
            <button
              className={`${styles.quizSession__button} ${styles['quizSession__button--primary']}`}
              onClick={handleRetry}
            >
              Retry Quiz
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={styles.quizSession}>
      <div className={styles.quizSession__header}>
        <h2 className={styles.quizSession__title}>{title}</h2>
        <p className={styles.quizSession__progress}>
          Question {currentIndex + 1} of {shuffledQuestions.length}
        </p>
        <div className={styles.quizSession__progressBar}>
          <motion.div
            className={styles.quizSession__progressFill}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          className={styles.quizSession__card}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
        >
          <QuestionRenderer
            question={currentQuestion}
            userAnswer={userAnswer}
            onAnswer={handleAnswer}
            showExplanation={showExplanation}
            isReviewMode={false}
            mathLiveReady={mathLiveReady}
          />

          {/* Submit button for fill-blank and mix-match questions */}
          {!showExplanation &&
            (submitQuestionTypes.includes(currentQuestion.question_type)) && (
              <motion.div
                className={styles.quizSession__actions}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <motion.button
                  className={`${styles.quizSession__button} ${styles['quizSession__button--primary']}`}
                  onClick={() => handleSubmit()}
                  disabled={!userAnswer}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Submit Answer
                </motion.button>
              </motion.div>
            )}

          {/* Next question button shown after answer is submitted */}
          {showExplanation && (
            <motion.div
              className={styles.quizSession__actions}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <motion.button
                className={`${styles.quizSession__button} ${styles['quizSession__button--primary']}`}
                onClick={handleNextQuestion}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {currentIndex < shuffledQuestions.length - 1 ? 'Next Question' : 'Finish Quiz'}
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
